<script setup lang="ts">
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import useAuthUser from "@/composables/useAuthUser";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import useCompany from "@/modules/companies/composables/useCompany";

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, provide, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AddressTab from "../pages/partials/edit/AddressTab.vue";
import CardsCompany from "../pages/partials/edit/CardsCompany.vue";
import CommentsTab from "../pages/partials/edit/CommentsTab.vue";
import EmailSettingsTab from "../pages/partials/edit/EmailSettingsTab.vue";
import CostsCentersTab from "../pages/partials/edit/CostsCentersTab.vue";
import CreditTab from "../pages/partials/edit/CreditTab.vue";
import DepartmentsTab from "../pages/partials/edit/DepartmentsTab.vue";
import FiscalTab from "../pages/partials/edit/FiscalTab.vue";
import GeneralTab from "../pages/partials/edit/GeneralTab.vue";
import MethodsPayment from "../pages/partials/edit/MethodsPayment.vue";
import NegotiationsTab from "../pages/partials/edit/NegotiationsTab.vue";
import RolesTab from "../pages/partials/edit/RolesTab.vue";
import UsersTab from "../pages/partials/edit/UsersTab.vue";

const pages = ref<Page[]>([]);
const selectedTabIndex = ref(0);

const route = useRoute();
const { params } = route;
const { user } = useAuthUser();
const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser();

const { company, isLoading, updateCompany, isFetching, refetch } = useCompany(
  {
    companyId: Number(params.id as string),
  },
  { immediate: true },
);

const tabs = [
  {
    slug: "general",
    name: "Información general",
    permission: [""],
    entityType: [""],
    component: GeneralTab,
  },
  {
    slug: "address",
    name: "Ubicación",
    permission: [""],
    entityType: [""],
    component: AddressTab,
  },
  {
    slug: "fiscal",
    name: "Datos fiscales",
    permission: [""],
    entityType: [""],
    component: FiscalTab,
  },
  {
    slug: "credit",
    name: "Crédito empresarial",
    permission: [""],
    entityType: [""],
    component: CreditTab,
  },
  {
    slug: "users",
    name: "Usuarios",
    permission: [""],
    entityType: [""],
    component: UsersTab,
  },
  {
    slug: "roles",
    name: "Roles",
    permission: [""],
    entityType: [""],
    component: RolesTab,
  },
  {
    slug: "cards",
    name: "Tarjetas de la empresa",
    permission: [""],
    entityType: [""],
    component: CardsCompany,
  },
  {
    slug: "methods",
    name: "Métodos de pago",
    permission: [""],
    entityType: [""],
    internalOnly: user.value?.internal,
    component: MethodsPayment,
  },
  {
    slug: "negotiations",
    name: "Negociaciones",
    permission: [""],
    entityType: [""],
    internalOnly: user.value?.internal,
    component: NegotiationsTab,
  },
  {
    slug: "departments",
    name: "Departamentos",
    permission: [""],
    entityType: [""],
    component: DepartmentsTab,
  },
  {
    slug: "costCenters",
    name: "Centros de costos",
    permission: [""],
    entityType: [""],
    component: CostsCentersTab,
  },
  {
    slug: "comments",
    name: "Comentarios",
    permission: [""],
    entityType: [""],
    component: CommentsTab,
  },
  // La configuración de correos de la empresa, para que la agencia pueda verla y
  // apagarla aunque el plan del cliente no incluya el módulo: ésas son justo las que
  // hoy no puede tocar nadie. Ver EmailSettingsTab del módulo interno.
  {
    slug: "emailSettings",
    name: "Configuración de correos",
    permission: [""],
    entityType: [""],
    component: EmailSettingsTab,
  },
];

const visibleTabs = computed(() =>
  tabs.filter((tab) =>
    tab.permission.some((permission, i) =>
      hasPermissionUser(permission, tab.entityType[i])
    )
  )
);

const onTabChange = (idx: number) => {
  selectedTabIndex.value = idx;
};

const queryClient = useQueryClient();

const invalidateQueryData = async () => {
  queryClient.invalidateQueries(["company", Number(params.id)]);
  await refetch();
};

provide("editCompanyProvides", { invalidateQueryData, isFetching });

watch(
  company,
  (value) => {
    pages.value = [
      { name: "Empresas", to: { name: "companies.index.internal" } },
      {
        name: value?.name || "",
        to: { name: "companies.edit.internal", params: { id: value?.id } },
        current: true,
      },
    ];
  },
  { immediate: true },
);

// watch(
//   abilities,
//   () => {
//     redirectTo404("update", "App\\Models\\Company");
//   },
//   {
//     immediate: true,
//   },
// );
</script>

<template>
  <BreadCrumb :pages="pages" />

  <TabGroup
    v-if="company && !isLoading"
    as="div"
    class="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto px-4 lg:px-[2.75rem] py-2"
    :selectedIndex="selectedTabIndex"
    @change="onTabChange"
  >
    <TabList
      as="aside"
      class="flex flex-col gap-4 lg:w-1/3 lg:max-w-[13.75rem] !lg:sticky top-20"
    >
      <RouterLink
        class="flex justify-center"
        :to="{
          name: 'companies.view',
          params: { name: company.name, id: company.id },
        }"
      >
        <img class="object-contain mb-2 max-h-[5rem]" :src="company.logo" />
      </RouterLink>

      <nav
        v-dragscroll.x
        class="no-scrollbar flex touch-pan-x px-2 lg:p-0 w-full lg:max-w-[13.75rem] flex-row lg:flex-col gap-2 overflow-x-auto"
      >
        <Tab
          v-for="(tab, i) in tabs.filter((tab) =>
            tab.permission.some((permission, i) => {
              return hasPermissionUser(permission, tab.entityType[i]);
            }),
          )"
          :key="i"
          v-slot="{ selected }"
          class="rounded-md w-[13.75rem]"
        >
          <button
            :class="[
              'tab fade-transition',
              selected ? 'bg-[#253C70]' : 'bg-[#1F2937]',
            ]"
          >
            {{ tab.name }}
          </button>
        </Tab>
      </nav>
    </TabList>

    <TabPanels as="div" class="w-full">
      <TabPanel
        v-for="(Element, index) in tabs.filter((tab) =>
          tab.permission.some((permission, i) => {
            return hasPermissionUser(permission, tab.entityType[i]);
          }),
        )"
        :key="index"
      >
        <component
          :is="Element.component"
          :company="company"
          :updateCompany="updateCompany"
        />
      </TabPanel>
    </TabPanels>
  </TabGroup>

  <LoadingModal :when="isLoading" />
</template>

<style scoped>
.tab {
  @apply w-full py-3 px-2 font-medium whitespace-nowrap rounded-md text-white focus:outline-none select-none;
}

.header {
  @apply font-semibold;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
